import { useEffect, useState } from 'react'
import { isDesktopApp, syncAlerts } from '../lib/desktop'
import { Bell, Plus, Trash2, ArrowUp, ArrowDown } from 'lucide-react'
import { PanelShell } from '../components/Layout'
import { Card, Button, Modal, Field, Empty, Rows, Badge, NumberInput } from '../components/UI'
import { CoinIcon } from '../components/Market'
import { get, post, del } from '../lib/api'
import { toast, useMarket, useTickers } from '../store'
import { clsx, num, faDate, asArray} from '../lib/format'

export default function Alerts() {
  const allTickers = useTickers()
  const tickers = useMarket((s) => s.tickers)
  const [rows, setRows] = useState<any[] | null>(null)
  const [open, setOpen] = useState(false)
  const [symbol, setSymbol] = useState('BTCIRT')
  const [direction, setDirection] = useState<'above' | 'below'>('above')
  const [target, setTarget] = useState('')
  const [channel, setChannel] = useState('email')
  const [loading, setLoading] = useState(false)

  /*
   * پس از هر بار خواندن، فهرست به نرم‌افزار ویندوز هم داده می‌شود.
   *
   * نرم‌افزار خودش قیمت‌ها را می‌پاید و وقتی هدفی رد شد اعلان ویندوز نشان
   * می‌دهد — حتی وقتی پنجره بسته و برنامه در سینی سیستم است. در مرورگر
   * معمولی `syncAlerts` بی‌صدا هیچ کاری نمی‌کند.
   */
  const load = () =>
    get('/user/alerts')
      .then((r: any) => {
        const list = asArray<any>(r)
        setRows(list)
        void syncAlerts(list.filter((a) => !a.triggered))
      })
      .catch(() => setRows([]))
  useEffect(() => { load() }, [])  // نه useEffect(load) — load یک Promise برمی‌گرداند
  useEffect(() => { if (tickers[symbol] && !target) setTarget(String(Math.round(tickers[symbol].last))) }, [symbol])

  const create = async () => {
    setLoading(true)
    try {
      await post('/user/alerts', { symbol, direction, target: Number(target), channel })
      toast.ok('هشدار قیمت ایجاد شد')
      setOpen(false); setTarget(''); load()
    } catch (e: any) { toast.err(e.message) } finally { setLoading(false) }
  }

  return (
    <PanelShell title="هشدار قیمت" subtitle="با رسیدن قیمت به مقدار دلخواه شما، اطلاع‌رسانی می‌شود">
      <div className="mb-5 flex justify-end">
        <Button onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> ایجاد هشدار جدید</Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {rows === null ? <div className="sm:col-span-2 lg:col-span-3"><Rows n={3} h="h-28" /></div>
          : rows.length === 0 ? <div className="sm:col-span-2 lg:col-span-3"><Card><Empty text="هشداری تعریف نشده است" icon={<Bell className="h-6 w-6" />} /></Card></div>
          : rows.map((a) => {
            const t = tickers[a.symbol]
            const progress = t ? Math.min(100, (t.last / a.target) * 100) : 0
            return (
              <Card key={a.id} className={clsx(a.triggered && 'border-up/30')}>
                <div className="mb-4 flex items-center gap-3">
                  <CoinIcon symbol={t?.base ?? a.symbol.slice(0, 3)} color={t?.color} size={34} />
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-white">{t?.faName ?? a.symbol}</div>
                    <div className="num text-[11px] text-slate-500" dir="ltr">{a.symbol}</div>
                  </div>
                  <button onClick={async () => { await del(`/user/alerts/${a.id}`); toast.ok('هشدار حذف شد'); load() }}
                    className="mr-auto rounded-lg p-2 text-slate-500 transition hover:bg-down/10 hover:text-down">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>

                <div className="mb-3 flex items-center gap-2 text-sm">
                  {a.direction === 'above' ? <ArrowUp className="h-4 w-4 text-up" /> : <ArrowDown className="h-4 w-4 text-down" />}
                  <span className="text-slate-400">{a.direction === 'above' ? 'بالاتر از' : 'پایین‌تر از'}</span>
                  <span className="num font-bold text-white">{num(a.target)}</span>
                </div>

                <div className="mb-2 h-1.5 overflow-hidden rounded-full bg-white/[.06]">
                  <div className={clsx('h-full rounded-full transition-all duration-700', a.direction === 'above' ? 'bg-up' : 'bg-down')}
                    style={{ width: `${Math.min(progress, 100)}%` }} />
                </div>
                <div className="flex items-center justify-between text-[11px]">
                  <span className="num text-slate-500">فعلی: {num(a.current)}</span>
                  {a.triggered ? <Badge kind="up">فعال شد</Badge> : <Badge kind="muted">در انتظار</Badge>}
                </div>
                <div className="mt-3 text-[11px] text-slate-600">{faDate(a.createdAt, false)}</div>
              </Card>
            )
          })}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="ایجاد هشدار قیمت">
        <Field label="بازار">
          <select className="input" value={symbol} onChange={(e) => { setSymbol(e.target.value); setTarget('') }}>
            {allTickers.map((t) => (
              <option key={t.symbol} value={t.symbol} className="bg-ink-900">
                {t.faName} — {t.quote === 'IRT' ? 'تومان' : t.quote}
              </option>
            ))}
          </select>
        </Field>
        <Field label="شرط">
          <div className="flex gap-2">
            {([['above', 'بالاتر از'], ['below', 'پایین‌تر از']] as const).map(([v, l]) => (
              <button key={v} onClick={() => setDirection(v)}
                className={clsx('chip flex-1 justify-center', direction === v && 'border-brand-500/50 bg-brand-500/15 text-brand-200')}>{l}</button>
            ))}
          </div>
        </Field>
        <Field label="قیمت هدف" hint={tickers[symbol] ? `قیمت فعلی: ${num(tickers[symbol].last)}` : ''}>
          <NumberInput value={target} onChange={setTarget} suffix={tickers[symbol]?.quote === 'IRT' ? 'تومان' : tickers[symbol]?.quote} />
        </Field>
        <Field label="روش اطلاع‌رسانی">
          <div className="flex flex-wrap gap-2">
            {[['email', 'ایمیل'], ['sms', 'پیامک'], ['push', 'اعلان مرورگر']].map(([v, l]) => (
              <button key={v} onClick={() => setChannel(v)}
                className={clsx('chip', channel === v && 'border-brand-500/50 bg-brand-500/15 text-brand-200')}>{l}</button>
            ))}
          </div>
          {/*
            در نرم‌افزار ویندوز، هشدار جدا از کانال انتخابی هم اعلان بومی
            می‌دهد. صریح گفته می‌شود چون کاربر باید بداند لازم نیست پیامک
            انتخاب کند — و پیامک برای صرافی هزینه دارد.
          */}
          {isDesktopApp() && (
            <p className="mt-2.5 text-[11px] leading-6 text-up">
              این هشدار در نرم‌افزار ویندوز هم به‌صورت اعلان روی صفحه نمایش داده
              می‌شود — حتی وقتی پنجره بسته باشد و برنامه در سینی سیستم باشد.
            </p>
          )}
        </Field>
        <Button loading={loading} onClick={create} className="w-full !py-3" disabled={!Number(target)}>ایجاد هشدار</Button>
      </Modal>
    </PanelShell>
  )
}
